import './instrument';
import * as Sentry from '@sentry/react';
import 'core-js/es/map';
import 'core-js/es/set';
import 'raf/polyfill';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import TagManager from 'react-gtm-module';
import { QueryClient, QueryClientProvider } from 'react-query';
import { combineComponents, ComponentWithProps } from 'utils/combineComponents';
import { ThemeProvider, ContentProvider, FeatureToggleProvider } from 'providers';
import Auth0 from 'hooks/Auth0/Auth0';
import NinetailedWrapper from 'components/NinetailedWrapper/NinetailedWrapper';
import SegmentProvider from 'contexts/segmentProvider/SegmentProvider';
import { SessionProvider } from 'contexts/sessionProvider/SessionProvider';
import { ANALYTICS_CONSTANTS } from './constants';

import App from './app/App';
import { ErrorPage } from './components/errorPage/ErrorPage';

const history = createBrowserHistory();

TagManager.initialize({
  gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
  dataLayerName: 'CheckoutDataLayer'
});

history.listen(() => {
  const windowRef: any = window;

  windowRef.dataLayer.push({
    event: ANALYTICS_CONSTANTS.ROUTE_CHANGE_EVENT
  });
});

const isIE = /* @cc_on!@ */!!document.documentMode;
const isAtLeastIE11 =
  !!(navigator.userAgent.match(/Trident/) && !navigator.userAgent.match(/MSIE/));

const OutdatedBrowserWarning = () => (
  <section className='outdated-browser fg-light' data-myob-outdated-browser data-closable>
    <div className='outdated-browser__body'>
      <div className='outdated-browser__copy'>Hmm, your browser&apos;s a little old.{' '}
        <a href='https://outdatedbrowser.com' target='_blank' rel='noreferrer'>
          Update here
        </a>
        {' '}for the best experience.
      </div>
    </div>
  </section>
);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});
const providers = [
  BrowserRouter,
  [QueryClientProvider, { client: queryClient }],
  FeatureToggleProvider,
  [Auth0, { audience: 'quipu-api' }],
  SessionProvider,
  ContentProvider,
  SegmentProvider,
  ThemeProvider,
  NinetailedWrapper
] as ComponentWithProps[];

const AllProviders = combineComponents(...providers);
const ComponentRender = (isIE && !isAtLeastIE11) ? (
  <OutdatedBrowserWarning/>
) : (
  <Sentry.ErrorBoundary fallback={<ErrorPage />}>
    <AllProviders>
      <App/>
    </AllProviders>
  </Sentry.ErrorBoundary>
);

const container = window.document.getElementById('checkout-ui-root');
const rootElement = createRoot(container!);
rootElement.render(ComponentRender);
